/**
 * PLATFORM-1 · Phase 6 — AI Ops widget for the Operations Hub dashboard.
 *
 * Polls /api/ai-ops/patterns every 30s for the active hotel and shows the
 * top 3 detected operational patterns (kitchen SLA cluster, refund cluster,
 * HK falling behind, etc.) as compact coloured chips. Purely descriptive —
 * no decisions.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radar, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useHotelContext } from "@/contexts/HotelContext";

function sevPill(severity) {
  switch (severity) {
    case "critical": return "bg-rose-500/20 text-rose-100 ring-rose-400/50";
    case "warning":  return "bg-amber-500/20 text-amber-100 ring-amber-400/50";
    default:         return "bg-slate-500/20 text-slate-100 ring-slate-400/50";
  }
}

function sevDot(severity) {
  switch (severity) {
    case "critical": return "bg-rose-400 animate-pulse";
    case "warning":  return "bg-amber-400";
    default:         return "bg-slate-400";
  }
}

export default function AIOpsWidget() {
  const { ctx } = useHotelContext() || {};
  const hotelId = ctx?.hotel_id;
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPatterns = useCallback(async () => {
    if (!hotelId) return;
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/ai-ops/patterns", {
        params: { hotel_id: hotelId, window_hours: 24 },
      });
      setPatterns(data.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load AI Ops patterns");
    } finally { setLoading(false); }
  }, [hotelId]);

  useEffect(() => {
    fetchPatterns();
    const iv = setInterval(fetchPatterns, 30000);
    return () => clearInterval(iv);
  }, [fetchPatterns]);

  const top3 = patterns.slice(0, 3);
  const criticalCount = patterns.filter((p) => p.severity === "critical").length;

  return (
    <div
      className="on-dark relative overflow-hidden bg-gradient-to-br from-[#0F1530] to-[#070B1F] rounded-2xl border border-white/10 p-4"
      data-testid="ai-ops-widget"
    >
      <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-hocp-gold/5 blur-2xl pointer-events-none"/>
      <div className="flex items-center gap-2 relative">
        <div className="w-8 h-8 rounded-lg bg-white/10 ring-1 ring-white/15 flex items-center justify-center text-hocp-gold">
          <Radar className="w-4 h-4" strokeWidth={1.75}/>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-300 font-medium">AI Ops · patterns</div>
          <div className="text-[13px] text-white font-semibold">Rolling 24-hour context</div>
        </div>
        <Link
          to="/operations-hub/feed"
          data-testid="ai-ops-widget-view-all"
          className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-300 hover:text-white"
        >
          Live feed <ArrowRight className="w-3 h-3"/>
        </Link>
      </div>

      <div className="mt-4 relative space-y-2 min-h-[112px]">
        {loading && patterns.length === 0 && (
          <div className="text-[12px] text-slate-400" data-testid="ai-ops-widget-loading">
            Scanning event stream…
          </div>
        )}
        {error && (
          <div className="text-[12px] text-rose-300" data-testid="ai-ops-widget-error">
            {error}
          </div>
        )}
        {!loading && !error && patterns.length === 0 && (
          <div className="text-[12.5px] text-slate-400" data-testid="ai-ops-widget-empty">
            All calm. No operational patterns flagged in the last 24h.
          </div>
        )}
        {top3.map((p, i) => (
          <div
            key={p.code}
            data-testid={`ai-ops-pattern-${p.code}`}
            className={`flex items-start gap-2 rounded-lg p-2 ring-1 ${sevPill(p.severity)}`}
          >
            <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${sevDot(p.severity)}`}/>
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] font-semibold leading-tight">{p.summary}</div>
              <div className="text-[10.5px] uppercase tracking-wider mt-0.5 opacity-70 font-mono">{p.code}</div>
            </div>
          </div>
        ))}
        {patterns.length > 3 && (
          <div className="text-[11px] text-slate-400 pt-1">
            +{patterns.length - 3} more {patterns.length - 3 === 1 ? "pattern" : "patterns"}{criticalCount > 3 ? ` · ${criticalCount} critical` : ""}
          </div>
        )}
      </div>
    </div>
  );
}
